import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../queryKeys";
import { apiUrls } from "src/consts/ApiUrls";
import { useParams } from "react-router-dom";
import useMyUser from "./useMyUser";
import { portalApiClient } from "../apiClient";
import { useMemo } from "react";

export type AssignmentResponse = {
  id: number;
};
export default function useAssignments() {
  const { companyId } = useParams();
  const { data: assignments } = useQuery({
    queryKey: [queryKeys.assignment.all()],
    queryFn: async () =>
      (await portalApiClient.get(apiUrls.assignment.company.url(companyId!)))
        .data,
    enabled: !!companyId,
  });
  return assignments;
}

export function useMyAssignments() {
  const { companyId } = useParams();
  const { data: myUser } = useMyUser();
  const { data: assignments } = useQuery({
    queryKey: [queryKeys.assignment.byUserId(companyId!, myUser?.id)],
    queryFn: async () =>
      (await portalApiClient.get(apiUrls.assignment.user.url(myUser?.id)))
        .data as AssignmentResponse[],
    enabled: !!myUser,
  });
  return assignments;
}

export function useAssignmentByInspectionId(id?: string) {
  const { companyId, variationId, inspectionId, deviationId } = useParams();
  const queryClient = useQueryClient();

  // Use inspectionId if available, otherwise fall back to variationId
  const actualInspectionId = useMemo(() => {
    if (id) return id;
    if (inspectionId) return inspectionId;
    if (variationId) return variationId;
    if (deviationId) return deviationId;
    return null;
  }, [inspectionId, variationId, deviationId]);

  const query = useQuery({
    queryKey: [
      queryKeys.assignment.byInspectionId(companyId!, actualInspectionId!),
    ],
    queryFn: async () =>
      (
        await portalApiClient.get(
          apiUrls.assignment.byInspectionId.url(actualInspectionId!)
        )
      ).data,
    enabled: !!actualInspectionId,
  });
  return {
    ...query,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: [queryKeys.assignment.all()],
      }),
  };
}
